import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Card,
  Col,
  Input,
  Pagination,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";

import useNuJobsStore from "../../../store/nujobs.store";
import "./nujobsUserAnalytics.scss";

const { Search } = Input;
const { Option } = Select;

export default function NUJobsUserAnalytics() {
  const { getUserAnalytics } = useNuJobsStore();
  const [loading, setLoading] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState("30days");

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeNow: 0,
    activeToday: 0,
    freeUsers: 0,
    monthlyUsers: 0,
    quarterlyUsers: 0,
    trialUsers: 0,
    expiredUsers: 0,
    neverActiveUsers: 0,
    totalSubscriptionAmount: 0,
  });

  const [users, setUsers] = useState([]);

  const [searchText, setSearchText] = useState("");

  const [planFilter, setPlanFilter] = useState("all");

  const [statusFilter, setStatusFilter] = useState("all");

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  /*
   * ============================================================
   * BACKEND API PLACEHOLDER
   * ============================================================
   */

  const loadUserAnalytics = async (
    page = 1,
    limit = 10,
    search = "",
    plan = "all",
    status = "all",
    period = selectedPeriod
  ) => {
    try {
      setLoading(true);

      const data = (await getUserAnalytics({
        page,
        limit,
        search,
        plan,
        status,
        period,
      })) || {};
      const summary = data.summary || {};

      setStats({
        totalUsers: summary.totalUsers || 0,
        activeNow: summary.onlineUsers || 0,
        activeToday: summary.activeToday || 0,
        freeUsers: summary.freeUsers || 0,
        monthlyUsers: summary.monthlyUsers || 0,
        quarterlyUsers: summary.quarterlyUsers || 0,
        trialUsers: summary.trialUsers || 0,
        expiredUsers: summary.expiredUsers || 0,
        neverActiveUsers: summary.neverActiveUsers || 0,
        totalSubscriptionAmount: summary.totalSubscriptionAmount || 0,
      });

      setUsers(
        (data.users || []).map((user, index) => ({
          ...user,
          key: user.nujobId || index,
          nujuid: user.nujobId,
          activeStatus: user.status,
          profileImage: user.profileImage || null,
        }))
      );

      setPagination({
        page: page,
        limit: limit,
        total: data.pagination?.total || summary.totalUsers || 0,
      });
    } catch (error) {
      console.error(
        "Error getting NuJobs user analytics",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserAnalytics(
      1,
      pagination.limit,
      searchText,
      planFilter,
      statusFilter,
      selectedPeriod
    );
  }, []);

  

  const handlePeriodChange = (value) => {
    setSelectedPeriod(value);

    loadUserAnalytics(
      1,
      pagination.limit,
      searchText,
      planFilter,
      statusFilter,
      value
    );
  };

  const getPlanTag = (plan) => {
    switch (plan) {
      case "basic":
        return (
          <Tag className="plan-tag plan-basic">
            Basic
          </Tag>
        );

      case "quarterly":
        return (
          <Tag className="plan-tag plan-quarterly">
            Quarterly
          </Tag>
        );

      case "monthly":
        return (
          <Tag className="plan-tag plan-monthly">
            Monthly
          </Tag>
        );

      case "trial":
        return (
          <Tag className="plan-tag plan-trial">
            Trial
          </Tag>
        );

      case "expired":
        return (
          <Tag className="plan-tag plan-expired">
            Expired
          </Tag>
        );

      default:
        return (
          <Tag className="plan-tag plan-free">
            Free
          </Tag>
        );
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "online":
        return (
          <Badge
            className="user-status-badge"
            status="success"
            text="Online"
          />
        );

      case "away":
        return (
          <Badge
            className="user-status-badge"
            color="#FFDE21"
            text="Away"
          />
        );

      default:
        return (
          <Badge
            className="user-status-badge"
            status="default"
            text="Offline"
          />
        );
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);

    loadUserAnalytics(
      1,
      pagination.limit,
      value,
      planFilter,
      statusFilter,
      selectedPeriod
    );
  };

  const handlePlanChange = (value) => {
    setPlanFilter(value);

    loadUserAnalytics(
      1,
      pagination.limit,
      searchText,
      value,
      statusFilter,
      selectedPeriod
    );
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);

    loadUserAnalytics(
      1,
      pagination.limit,
      searchText,
      planFilter,
      value,
      selectedPeriod
    );
  };

  const handlePageChange = (page, pageSize) => {
    setPagination({
      ...pagination,
      page,
      limit: pageSize,
    });

    loadUserAnalytics(
      page,
      pageSize,
      searchText,
      planFilter,
      statusFilter,
      selectedPeriod
    );
  };

  /*
   * ============================================================
   * TABLE COLUMNS
   * ============================================================
   */

  const columns = [
    {
      title: "Profile",
      dataIndex: "profileImage",
      key: "profileImage",
      width: 80,
      render: (image, record) => (
        <Avatar
          className="analytics-user-avatar"
          size={42}
          src={image}
          icon={!image && <UserOutlined />}
        >
          {!image &&
            record?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
      ),
    },

    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <span className="analytics-user-name">
          {name}
        </span>
      ),
    },

    {
      title: "NuJobs ID",
      dataIndex: "nujuid",
      key: "nujuid",
      render: (id) => (
        <span className="analytics-user-id">
          {id}
        </span>
      ),
    },

    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => (
        <span className="analytics-user-email">
          {email}
        </span>
      ),
    },

    {
      title: "Status",
      dataIndex: "activeStatus",
      key: "activeStatus",
      render: (status) =>
        getStatusBadge(status),
    },

    {
      title: "Plan",
      dataIndex: "plan",
      key: "plan",
      render: (plan) =>
        getPlanTag(plan),
    },

    {
      title: "Last Active",
      dataIndex: "lastActive",
      key: "lastActive",
      render: (value) => (
        <span className="analytics-last-active">
          {value}
        </span>
      ),
    },
  ];

  return (
    <div className="nujobs-user-analytics-wrapper">

      {/* TITLE */}

      <div className="analytics-title">
        NuJobs User Analytics
      </div>

      <div className="analytics-subtitle">
        Monitor NuJobs users, activity and subscription usage
      </div>


      {/* =====================================================
          PERIOD + SUBSCRIPTION AMOUNT
      ====================================================== */}

      <div className="analytics-period-section">

        <div className="analytics-period-selector">

          <span className="analytics-period-label">
            Period
          </span>

          <Select
            className="analytics-period-select"
            value={selectedPeriod}
            onChange={handlePeriodChange}
          >
            <Option value="today">
              Today
            </Option>

            <Option value="7days">
              Last 7 Days
            </Option>

            <Option value="30days">
              Last 30 Days
            </Option>

            <Option value="3months">
              Last 3 Months
            </Option>

            <Option value="6months">
              Last 6 Months
            </Option>

            <Option value="1year">
              Last 1 Year
            </Option>
          </Select>

        </div>


        <div className="analytics-subscription-amount">

          <div className="subscription-amount-label">
            Total Subscription Amount
          </div>

          <div className="subscription-amount-value">
            ₹
            {Number(
              stats.totalSubscriptionAmount || 0
            ).toLocaleString("en-IN")}
          </div>

        </div>

      </div>


      {/* STATISTICS */}

      <div className="analytics-statistics">

        <Row gutter={[16, 16]}>

          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card className="analytics-stat-card">
              <Statistic
                className="analytics-statistic"
                title="Total Users"
                value={stats.totalUsers}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card className="analytics-stat-card">
              <Statistic
                className="analytics-statistic active-statistic"
                title="Active Now"
                value={stats.activeNow}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card className="analytics-stat-card">
              <Statistic
                className="analytics-statistic"
                title="Active Today"
                value={stats.activeToday}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card className="analytics-stat-card">
              <Statistic
                className="analytics-statistic"
                title="Free Plan"
                value={stats.freeUsers}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card className="analytics-stat-card">
              <Statistic
                className="analytics-statistic"
                title="Monthly Plan"
                value={stats.monthlyUsers}
                prefix={<CrownOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card className="analytics-stat-card">
              <Statistic
                className="analytics-statistic"
                title="Quarterly Plan"
                value={stats.quarterlyUsers}
                prefix={<CrownOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card className="analytics-stat-card">
              <Statistic
                className="analytics-statistic"
                title="Trial Users"
                value={stats.trialUsers}
                prefix={<ExperimentOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card className="analytics-stat-card">
              <Statistic
                className="analytics-statistic"
                title="Expired"
                value={stats.expiredUsers}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card className="analytics-stat-card">
              <Statistic
                className="analytics-statistic"
                title="Never Active"
                value={stats.neverActiveUsers}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>

        </Row>

      </div>


      {/* USERS */}

      <div className="analytics-users-section">

        <div className="analytics-users-title">
          NuJobs Users
        </div>


        {/* FILTERS */}

        <div className="analytics-filters">

          <Search
            className="analytics-search"
            placeholder="Search Name, Email or NuJobs ID"
            allowClear
            onSearch={handleSearch}
          />

          <Select
            className="analytics-filter"
            value={planFilter}
            onChange={handlePlanChange}
          >
            <Option value="all">
              All Plans
            </Option>

            <Option value="free">
              Free
            </Option>

            <Option value="basic">
              Basic
            </Option>

            <Option value="monthly">
              Monthly
            </Option>

            <Option value="quarterly">
              Quarterly
            </Option>

            <Option value="trial">
              Trial
            </Option>

            <Option value="expired">
              Expired
            </Option>
          </Select>


          <Select
            className="analytics-filter"
            value={statusFilter}
            onChange={handleStatusChange}
          >
            <Option value="all">
              All Status
            </Option>

            <Option value="online">
              Online
            </Option>

            <Option value="away">
              Away
            </Option>

            <Option value="offline">
              Offline
            </Option>
          </Select>

        </div>


        {/* TABLE */}

        <div className="analytics-table-wrapper">

          <Table
            className="analytics-user-table"
            columns={columns}
            dataSource={users}
            loading={loading}
            pagination={false}
            scroll={{ x: 850 }}
            rowKey="nujuid"
          />

        </div>


        {/* PAGINATION */}

        <div className="analytics-pagination">

          <Pagination
            className="analytics-pagination-control"
            current={pagination.page}
            pageSize={pagination.limit}
            total={pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} users`
            }
            onChange={handlePageChange}
          />

        </div>

      </div>

    </div>
  );
}